import React from 'react';
import { Select } from 'antd';
import { Tag } from 'api/useTags';
import { getSelectedTagColor } from './TagsUtil';

interface TagsSelectProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  tags: Tag[];
  placeholder?: string;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
  allowClear?: boolean;
  showSearch?: boolean;
  mode?: 'multiple' | 'tags';
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  allowUserTagCreation?: boolean;
}

const TagsSelect: React.FC<TagsSelectProps> = ({
  value,
  onChange,
  tags,
  placeholder = 'Select a tag',
  size = 'large',
  disabled = false,
  allowClear = true,
  showSearch = true,
  mode = 'multiple',
  className,
  onKeyDown,
  allowUserTagCreation
}) => {
  const tagRender = (props: any) => {
    const { label, closable, onClose } = props;

    // Extract the actual text content from the label
    const getLabelText = (label: any): string => {
      if (typeof label === 'string') {
        return label;
      }
      // If label is a React element, try to extract text from it
      if (label && typeof label === 'object') {
        // Handle case where label might be a React element with children
        if (label.props && label.props.children) {
          const children = label.props.children;
          if (Array.isArray(children)) {
            // Find the text content (usually the last child)
            const textChild = children.find((child) => typeof child === 'string');
            if (textChild) return textChild;
          } else if (typeof children === 'string') {
            return children;
          }
        }
        // Fallback: try to get the label from the option's label prop
        return String(label);
      }
      return String(label);
    };

    const labelText = getLabelText(label);
    const colors = getSelectedTagColor(labelText, tags);

    return (
      <div
        className={`flex items-center gap-1 px-2 py-1 ${colors.bg} ${colors.text} rounded-2xl text-xs min-h-6`}
        style={colors.bgStyle ? { [colors.bgStyle.split(':')[0]]: colors.bgStyle.split(':')[1].trim() } : undefined}
      >
        <span className="font-medium">{labelText}</span>
        {closable && (
          <span
            className={`cursor-pointer ml-1 text-sm font-bold flex items-center justify-center w-4 h-4 rounded-full ${colors.closeBg} ${colors.icon}`}
            onClick={onClose}
          >
            ×
          </span>
        )}
      </div>
    );
  };

  return (
    <div>
      <Select
        mode={mode}
        placeholder={placeholder}
        size={size}
        allowClear={allowClear}
        showArrow
        virtual={false}
        showSearch={showSearch}
        optionFilterProp="label"
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={className}
        tagRender={tagRender}
        onKeyDown={onKeyDown}
      >
        {tags.map((tag) => (
          <Select.Option key={tag.id} value={tag.id} label={tag.name}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: tag.color,
                  borderRadius: '2px'
                }}
              ></div>
              {tag.name}
            </div>
          </Select.Option>
        ))}
      </Select>
      {allowUserTagCreation && (
        <div className="text-xs text-gray-500 mt-1">To add a new tag, please type it in and press the Enter key.</div>
      )}
    </div>
  );
};

export default TagsSelect;
